import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

function WorkshopEmbedding({ workshop, onClose }) {
  const { currentUser } = useAuth();
  const [embedSettings, setEmbedSettings] = useState({
    borderRadius: 12,
    primaryColor: '#FFB27D',
    backgroundColor: '#1A1A1A',
    textColor: '#FFFFFF',
    fontFamily: 'Inter',
    showImage: true,
    showPrice: true,
    showDate: true,
    showLocation: true,
    width: '100%',
    height: 'auto'
  });
  const [copied, setCopied] = useState(false);

  const fontOptions = [
    { value: 'Inter', label: 'Inter (Default)' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Montserrat', label: 'Montserrat' }
  ];

  const generateEmbedCode = () => {
    const settings = encodeURIComponent(JSON.stringify(embedSettings));
    const embedUrl = `${window.location.origin}/embed/workshop/${workshop.id}?settings=${settings}`;
    
    return `<div class="sessions-workshop-embed" data-workshop-id="${workshop.id}" data-settings='${settings}'>
  <iframe 
    src="${embedUrl}" 
    style="width: ${embedSettings.width}; height: ${embedSettings.height}; border: none; border-radius: ${embedSettings.borderRadius}px;"
    allow="payment"
    title="Book ${workshop.title}"
  ></iframe>
</div>`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'TBD';
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'TBD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getLowestPrice = () => {
    if (workshop.packages && workshop.packages.length > 0) {
      const prices = workshop.packages
        .map(pkg => parseFloat(pkg.price))
        .filter(price => !isNaN(price));
      return prices.length > 0 ? Math.min(...prices) : null;
    }
    return workshop.price || null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-klorah-dark border border-klorah-border rounded-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-klorah-border">
          <div>
            <h2 className="text-2xl font-bold text-white">Workshop Embedding</h2>
            <p className="text-klorah-text">Customize and embed your workshop on your website</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-klorah-text hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Panel - Customization */}
          <div className="w-1/3 p-6 border-r border-klorah-border overflow-y-auto">
            <div className="space-y-6">
              {/* Visual Settings */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Visual Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Border Radius
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="24"
                      value={embedSettings.borderRadius}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, borderRadius: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-klorah-card rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-klorah-text-light mt-1">
                      <span>0px</span>
                      <span>{embedSettings.borderRadius}px</span>
                      <span>24px</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Primary Color
                    </label>
                    <input
                      type="color"
                      value={embedSettings.primaryColor}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="w-full h-10 rounded-lg border border-klorah-border bg-transparent cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Background Color
                    </label>
                    <input
                      type="color"
                      value={embedSettings.backgroundColor}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="w-full h-10 rounded-lg border border-klorah-border bg-transparent cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Text Color
                    </label>
                    <input
                      type="color"
                      value={embedSettings.textColor}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, textColor: e.target.value }))}
                      className="w-full h-10 rounded-lg border border-klorah-border bg-transparent cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Font Family
                    </label>
                    <select
                      value={embedSettings.fontFamily}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, fontFamily: e.target.value }))}
                      className="w-full px-3 py-2 bg-klorah-card border border-klorah-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                    >
                      {fontOptions.map(font => (
                        <option key={font.value} value={font.value}>{font.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Content Settings */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Content Settings</h3>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={embedSettings.showImage}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, showImage: e.target.checked }))}
                      className="mr-3 w-4 h-4 text-klorah-primary bg-klorah-card border-klorah-border rounded focus:ring-klorah-primary"
                    />
                    <span className="text-klorah-text">Show workshop image</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={embedSettings.showPrice}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, showPrice: e.target.checked }))}
                      className="mr-3 w-4 h-4 text-klorah-primary bg-klorah-card border-klorah-border rounded focus:ring-klorah-primary"
                    />
                    <span className="text-klorah-text">Show price</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={embedSettings.showDate}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, showDate: e.target.checked }))}
                      className="mr-3 w-4 h-4 text-klorah-primary bg-klorah-card border-klorah-border rounded focus:ring-klorah-primary"
                    />
                    <span className="text-klorah-text">Show date</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={embedSettings.showLocation}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, showLocation: e.target.checked }))}
                      className="mr-3 w-4 h-4 text-klorah-primary bg-klorah-card border-klorah-border rounded focus:ring-klorah-primary"
                    />
                    <span className="text-klorah-text">Show location</span>
                  </label>
                </div>
              </div>

              {/* Size Settings */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Size Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Width
                    </label>
                    <input
                      type="text"
                      value={embedSettings.width}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, width: e.target.value }))}
                      className="w-full px-3 py-2 bg-klorah-card border border-klorah-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                      placeholder="e.g., 100%, 400px"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-klorah-text mb-2">
                      Height
                    </label>
                    <input
                      type="text"
                      value={embedSettings.height}
                      onChange={(e) => setEmbedSettings(prev => ({ ...prev, height: e.target.value }))}
                      className="w-full px-3 py-2 bg-klorah-card border border-klorah-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                      placeholder="e.g., auto, 600px"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Preview and Code */}
          <div className="flex-1 flex flex-col">
            {/* Preview */}
            <div className="p-6 border-b border-klorah-border">
              <h3 className="text-lg font-semibold text-white mb-4">Live Preview</h3>
              <div 
                className="bg-white rounded-lg overflow-hidden shadow-lg"
                style={{ 
                  borderRadius: `${embedSettings.borderRadius}px`,
                  fontFamily: embedSettings.fontFamily,
                  backgroundColor: embedSettings.backgroundColor,
                  color: embedSettings.textColor
                }}
              >
                <div className="p-6">
                  {embedSettings.showImage && workshop.imageUrl && (
                    <div className="mb-4">
                      <img 
                        src={workshop.imageUrl} 
                        alt={workshop.title}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  
                  <h4 className="text-xl font-bold mb-3" style={{ color: embedSettings.textColor }}>
                    {workshop.title}
                  </h4>
                  
                  <p className="text-sm mb-4 opacity-80">
                    {workshop.shortDescription || workshop.description?.substring(0, 120) + '...'}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    {embedSettings.showDate && (
                      <div className="flex items-center text-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(workshop.startDate)} - {formatDate(workshop.endDate)}
                      </div>
                    )}
                    
                    {embedSettings.showLocation && (
                      <div className="flex items-center text-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {workshop.location}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    {embedSettings.showPrice && (
                      <div className="text-2xl font-bold" style={{ color: embedSettings.primaryColor }}>
                        {formatPrice(getLowestPrice())}
                      </div>
                    )}
                    
                    <button 
                      className="px-6 py-2 rounded-lg font-semibold text-black transition-all hover:opacity-90"
                      style={{ backgroundColor: embedSettings.primaryColor }}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Embed Code */}
            <div className="flex-1 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Embed Code</h3>
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    copied 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-klorah-primary text-black hover:opacity-90'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
              
              <div className="bg-klorah-card border border-klorah-border rounded-lg p-4">
                <pre className="text-sm text-klorah-text overflow-x-auto whitespace-pre-wrap">
                  <code>{generateEmbedCode()}</code>
                </pre>
              </div>
              
              <div className="mt-4 p-4 bg-klorah-card/50 border border-klorah-border rounded-lg">
                <h4 className="text-sm font-semibold text-white mb-2">How to use:</h4>
                <ol className="text-sm text-klorah-text space-y-1">
                  <li>1. Copy the embed code above</li>
                  <li>2. Paste it into your website's HTML</li>
                  <li>3. The workshop booking widget will appear on your site</li>
                  <li>4. Visitors can book directly from your website</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkshopEmbedding;
